'use client';

import { Calendar, type CalendarProps } from '@magicschool/spellbook/components/Calendar';
import { DateInputLeadingAdornmentSlot } from '@magicschool/spellbook/components/DateInput/slots';
import { IconButton } from '@magicschool/spellbook/components/IconButton';
import { Label } from '@magicschool/spellbook/components/Label';
import { Popover } from '@magicschool/spellbook/components/Popover';
import type { TextInputProps } from '@magicschool/spellbook/components/TextInput';
import { TextInput } from '@magicschool/spellbook/components/TextInput';
import { useId } from '@magicschool/spellbook/hooks/useId';
import { useSlots } from '@magicschool/spellbook/hooks/useSlots';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';

export interface DateInputText {
  /** The visible or screen-reader label for the input field. Defaults to 'Date'. */
  label?: string;
  /** The placeholder text for the input field. Defaults to 'Select a date'. */
  placeholder?: string;
  /** The accessible label for the calendar button. Defaults to 'Open calendar'. */
  calendarButtonLabel?: string;
}

export type DateInputProps = Omit<TextInputProps, 'type' | 'value' | 'defaultValue' | 'onChange'> & {
  /** When `true`, renders the label visibly above the input. When `false` (default), the label is visually hidden via `sr-only`. */
  showLabel?: boolean;
  /** The selected date. */
  value?: Date;
  /** The default selected date. */
  defaultValue?: Date;
  /** Callback fired when the date changes. */
  onDateChange?: (date: Date | undefined) => void;
  /** The language locale to use for formatting dates. Set to 'en-US' by default. See [MDN](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat#locales_and_options) for more information on locales. */
  locale?: string;
  /** Whether to hide the datepicker and its trigger button. */
  hideDatepicker?: boolean;
  /** Configuration for component default text content, labels, placeholders, and screen reader announcements. */
  text?: DateInputText;
  /** Additional props for the calendar component. See `CalendarProps` for more details. */
  calendarProps?: {
    className?: CalendarProps['className'];
    onSelect?: CalendarProps['onSelect'];
    formatters?: CalendarProps['formatters'];
    components?: CalendarProps['components'];
    defaultMonth?: CalendarProps['defaultMonth'];
    onMonthChange?: CalendarProps['onMonthChange'];
    onNextClick?: CalendarProps['onNextClick'];
    onPrevClick?: CalendarProps['onPrevClick'];
    startMonth?: CalendarProps['startMonth'];
    endMonth?: CalendarProps['endMonth'];
  };
};

const ISO_CALENDAR_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

// UTC-midnight values are calendar days built in UTC; any other instant is read in local time.
const localMidnight = (year: number, monthIndex: number, day: number) => {
  const date = new Date(0);
  date.setFullYear(year, monthIndex, day);
  date.setHours(0, 0, 0, 0);
  return date;
};

const toLocalCalendarDay = (date: Date) =>
  date.getTime() % 86_400_000 === 0
    ? localMidnight(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
    : localMidnight(date.getFullYear(), date.getMonth(), date.getDate());

const isValidDate = (date: unknown): date is Date => date instanceof Date && !Number.isNaN(date.getTime());

const DateInputBase = forwardRef<HTMLInputElement, DateInputProps>(
  (
    {
      children,
      className,
      id: providedId,
      labelId,
      showLabel = false,
      size = 'md',
      defaultValue,
      value,
      onDateChange,
      locale = 'en-US',
      calendarProps = {},
      hideDatepicker = false,
      text = {},
      ...props
    },
    forwardedRef,
  ) => {
    const { label = 'Date', placeholder = 'Select a date', calendarButtonLabel = 'Open calendar' } = text;
    const id = useId(providedId);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [internalDate, setInternalDate] = useState<Date | undefined>(defaultValue);
    const [open, setOpen] = useState(false);
    const isControlled = value !== undefined;
    const currentDate = isControlled ? value : internalDate;
    const calendarDay = isValidDate(currentDate) ? toLocalCalendarDay(currentDate) : undefined;

    const formatDateForInput = (date?: Date | string | unknown) => {
      if (isValidDate(date)) {
        const day = toLocalCalendarDay(date);
        const pad = (part: number, length = 2) => String(part).padStart(length, '0');
        return `${pad(day.getFullYear(), 4)}-${pad(day.getMonth() + 1)}-${pad(day.getDate())}`;
      }
      if (typeof date === 'string') {
        return date;
      }
      return '';
    };

    const parseDateFromInput = (value: string) => {
      const match = ISO_CALENDAR_DATE.exec(value);
      // A partly typed year such as 0002 stays year 2; new Date(2, …) would read it as 1902.
      return match ? localMidnight(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : undefined;
    };

    const handleSelect = (selectedDate: Date | undefined) => {
      if (selectedDate) {
        if (!isControlled) {
          setInternalDate(selectedDate);
        }
        onDateChange?.(selectedDate);
        setOpen(false);
      }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newDate = parseDateFromInput(e.target.value);
      if (!isControlled) {
        setInternalDate(newDate);
      }
      onDateChange?.(newDate);
    };

    const { slots } = useSlots(children, {
      leadingAdornment: { Component: DateInputLeadingAdornmentSlot, props: { ...(props.disabled && { color: 'disabled' }) } },
    });

    useImperativeHandle(forwardedRef, () => inputRef.current!, []);

    return (
      <div className="flex flex-col gap-1 w-full group/date-input">
        {!labelId && (
          <Label {...(!showLabel && { className: 'sr-only' })} htmlFor={id}>
            {label}
          </Label>
        )}
        <TextInput
          aria-labelledby={labelId}
          {...props}
          data-spellbook-name="date-input"
          ref={inputRef}
          type="date"
          id={id}
          className={className}
          size={size}
          value={formatDateForInput(currentDate)}
          onChange={handleInputChange}
          placeholder={placeholder}
          aria-haspopup={!hideDatepicker ? 'dialog' : undefined}
          lang={locale}
        >
          {slots.leadingAdornment && <TextInput.LeadingAdornment>{slots.leadingAdornment}</TextInput.LeadingAdornment>}
          <TextInput.TrailingAdornment overrideDefault>
            <Popover open={open} onOpenChange={setOpen} modal>
              <Popover.Trigger asChild>
                {slots.calendarButton ? (
                  slots.calendarButton
                ) : hideDatepicker ? null : (
                  <IconButton
                    name="calendar-days"
                    color="neutral"
                    variant="ghost"
                    size="sm"
                    shape="circle"
                    aria-label={calendarButtonLabel}
                    disabled={props.disabled || props.readOnly}
                  />
                )}
              </Popover.Trigger>
              <Popover.Content>
                <Calendar
                  selected={calendarDay}
                  defaultMonth={calendarDay || new Date()}
                  onSelect={handleSelect}
                  locale={locale}
                  disabled={props.disabled || props.readOnly}
                  {...calendarProps}
                  autoFocus
                />
              </Popover.Content>
            </Popover>
          </TextInput.TrailingAdornment>
        </TextInput>
      </div>
    );
  },
);

/**
 * Date input that combines an HTML5 date field with a calendar popover, allowing users to type a date or pick from a calendar.
 *
 * ### When to use
 * - Date entry fields where users may want to type directly or select from a visual calendar
 * - Forms requiring a single date value (e.g., start date, end date, birthdate)
 *
 * ### When NOT to use
 * - Date range selection (two dates): use two `DateInput` fields paired together
 * - Time-only entry: use `TextInput` with `type="time"` instead
 * - Date and time combined: pair `DateInput` with a separate time input
 *
 * ### API
 * - `size`: Input dimensions: `md` (default) for standard forms, `lg` for prominent inputs, `xl` for extra-large inputs
 * - `value`: Controlled selected `Date` object. Pass `undefined` for no selection.
 * - `defaultValue`: Uncontrolled default `Date` object
 * - `onDateChange`: Callback fired with the selected `Date` (or `undefined` when cleared)
 * - `locale`: BCP 47 locale string for date formatting (default: `en-US`)
 * - `showLabel`: When `true`, renders the label visibly. When `false` (default), the label is visually hidden via `sr-only`.
 * - `labelId`: Id of an external label (set by `Form.Group`). The input is named by it and the built-in label is omitted, so it has one name, not two.
 * - `hideDatepicker`: When `true`, hides the calendar button and popover, leaving only the native date input
 * - `calendarProps`: Passes configuration to the inner `Calendar` component (e.g., `startMonth`, `endMonth` to constrain the date range)
 * - `text.label`: Visible or screen-reader label text. Defaults to "Date".
 * - `text.placeholder`: Input placeholder text. Defaults to "Select a date".
 * - `text.calendarButtonLabel`: Accessible label for the calendar button. Defaults to "Open calendar".
 *
 * ### Composition (slots)
 * - `<DateInput.LeadingAdornment>`: Icon before the input. Accepts `IconProps`. Only include when adding a custom leading icon — not rendered by default.
 *
 * ### Visual states
 * - **Empty**: Placeholder text displayed, calendar button in trailing position
 * - **Has value**: Formatted date displayed in the input
 * - **Calendar open**: Popover with calendar picker, `aria-haspopup="dialog"` on input
 * - **Disabled**: Muted styling, calendar button disabled
 * - **Read-only**: Calendar button disabled, input non-editable
 *
 * ### Accessibility
 * - Includes an accessible `<Label>` (visible or `sr-only` based on `showLabel`) unless `labelId` points at an external label, as `Form.Group` does
 * - Calendar button has `aria-label` via `text.calendarButtonLabel` (default: "Open calendar")
 * - Input uses `lang` attribute matching the `locale` prop for proper date formatting by assistive technology
 *
 * ### Date handling
 * - Accepts and returns JavaScript `Date` objects — never pass raw strings to `value` or `defaultValue`
 * - Construct local dates with `new Date(year, monthIndex, day)`. Avoid `new Date('YYYY-MM-DD')` which parses as UTC midnight and can cause off-by-one day issues in timezones west of UTC.
 * - Use `date-fns` for date manipulation: `parseISO()` to hydrate ISO strings from APIs, `format()` for display formatting, `addYears()`/`addMonths()` for calendar range constraints
 * - `onDateChange` always returns local midnight of the chosen day, whether typed or picked. A UTC-midnight `value` is displayed as its UTC calendar day; any other `value` as its local day.
 * - Extra props are applied before the component's own, so a spread such as react-hook-form's `field` cannot replace the internal `onChange`, `value`, or `type`.
 * - For database storage, convert the `Date` from `onDateChange` to an ISO string (e.g., combine with a time value and call `.toISOString()`)
 */
export const DateInput = Object.assign(DateInputBase, {
  LeadingAdornment: DateInputLeadingAdornmentSlot,
});
